import { useState, useCallback } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

export const useBots = () => {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [botServiceEnabled, setBotServiceEnabled] = useState(false); // Initial state, should be checked on load

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchBots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/bots`, {
        headers: getAuthHeaders()
      });
      
      if (res.status === 403) {
          // Bot service not enabled or api_key missing - show enable button
          setBotServiceEnabled(false);
          setBots([]);
          // Don't set error for 403 - just show the enable button
          return;
      }

      const data = await res.json();
      if (data.success) {
        setBots(data.data || []);
        setBotServiceEnabled(true); // If we can fetch bots, service is enabled
      } else {
        // If server says not enabled, show enable button without error
        if (data.message?.includes('not enabled')) {
          setBotServiceEnabled(false);
        } else {
          setError(data.message);
        }
      }
    } catch (err) {
      // Network errors - still show enable button, don't show scary error
      console.error('fetchBots error:', err);
      setBotServiceEnabled(false);
      // Don't set error - just show the enable button
    } finally {
      setLoading(false);
    }
  }, []);

  const enableBotService = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/bots/enable`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setBotServiceEnabled(true);
        // Refresh bots list (might be empty initially)
        await fetchBots();
        return true;
      } else {
        setError(data.message);
        return false;
      }
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchBots]);

  const createBot = useCallback(async (name, systemPrompt) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/bots/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name, system_prompt: systemPrompt })
      });
      
      // If 403, user needs to re-enable bot service
      if (res.status === 403) {
        setBotServiceEnabled(false);
        setError('Please activate the bot service first');
        return false;
      }
      
      const data = await res.json();
      if (data.success) {
        setBots(prev => [data.data, ...prev]);
        return true;
      } else {
        setError(data.message);
        return false;
      }
    } catch (err) {
      console.error('createBot error:', err);
      setError('Failed to create bot. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    bots,
    loading,
    error,
    botServiceEnabled,
    fetchBots,
    enableBotService,
    createBot
  };
};
